<template src="./Risk.html"></template>

<script>
import { mapState, mapGetters } from "vuex";
import html2canvas from "html2canvas";

import RiskIndicatorChips from "@/components/DataAnalysis/Risk/RiskIndicatorChips/RiskIndicatorChips.vue";
import RiskControls from "@/components/DataAnalysis/Risk/RiskControls/RiskControls.vue";
import RiskPeriodSummary from "@/components/DataAnalysis/Risk/RiskPeriodSummary/RiskPeriodSummary.vue";
import RiskProbabilityChart from "@/components/DataAnalysis/Risk/RiskProbabilityChart/RiskProbabilityChart.vue";
import RiskWeeklyTable from "@/components/DataAnalysis/Risk/RiskWeeklyTable/RiskWeeklyTable.vue";
import RiskMessageCard from "@/components/DataAnalysis/Risk/RiskMessageCard/RiskMessageCard.vue";
import RiskSkeleton from "@/components/DataAnalysis/Risk/RiskSkeleton/RiskSkeleton.vue";

import { downloadRiskCsv } from "./riskCsv";
import { cacheKey } from "@/store/modules/risk/actions";

export default {
  name: "Risk",
  // Mirrors Dashboard's child-tab contract (dashboardActiveTab/onLoadDataClick
  // /startDate/endDate) plus tabIndex, since this tab isn't hardcoded to a
  // single index the way Dashboard's `== 0` check is.
  props: {
    riskActiveTab: { type: Number, default: null },
    tabIndex: { type: Number, default: null },
    onLoadDataClick: { type: Boolean, default: false },
    startDate: { type: String, default: null },
    endDate: { type: String, default: null },
    // DataAnalysis#lastPosition — where the marker was when LOAD DATA was last pressed,
    // NOT the live marker. Dragging the pin must not silently repoint this tab at a
    // location the user has not asked for; DataAnalysis already shows the "You have
    // placed the Marker over another location" banner above the tabs while the two
    // disagree.
    loadedPosition: { type: Object, default: () => ({ latitude: null, longitude: null }) },
  },
  components: {
    RiskIndicatorChips,
    RiskControls,
    RiskPeriodSummary,
    RiskProbabilityChart,
    RiskWeeklyTable,
    RiskMessageCard,
    RiskSkeleton,
  },
  data() {
    return {
      isNarrow: false,
      loadingChart: false,
    };
  },
  computed: {
    ...mapState({
      mapLatLon: (state) => state.mapLatLon,
    }),
    ...mapState("risk", ["indicator", "status", "data", "error", "units", "catalogStatus", "lastParams"]),
    ...mapGetters("risk", [
      "catalogForChips",
      "activeIndicator",
      "threshold",
      "op",
      "category",
      "minHours",
      "smallSample",
      "viewState",
      "chartBars",
      "periodSummary",
      "provenanceLines",
      "exportPayload",
    ]),
    isActiveTab() {
      return this.riskActiveTab === this.tabIndex;
    },
    hasPin() {
      return this.loadedPosition.latitude != null;
    },
    // A marker exists but has never been loaded — the empty card says so rather than
    // asking for a pin that is already there.
    pinPending() {
      return this.mapLatLon.latitude != null && !this.hasPin;
    },
    // Everything a request needs is in place, so "Calculate risk" can run.
    canCalculate() {
      return this.hasPin && !!this.startDate && !!this.endDate && this.threshold != null;
    },
    // The controls no longer fetch on their own: changing any of them leaves the results
    // on screen describing the LAST calculation, not the current settings. Compared
    // through the store's own cache key, so it counts exactly what makes a request
    // different (indicator, threshold, op, level, pin, dates).
    needsCalculate() {
      if (!this.canCalculate) return false;
      if (!this.lastParams) return true;
      return cacheKey(this.requestParams()) !== cacheKey(this.lastParams);
    },
    climatologyWindowLabel() {
      const cw = this.data && this.data.provenance && this.data.provenance.climatology_window;
      return cw ? `${cw.start_year}–${cw.end_year}` : "";
    },
  },
  watch: {
    riskActiveTab() {
      if (this.isActiveTab) this.maybeFetch();
    },
    onLoadDataClick() {
      if (this.isActiveTab) this.maybeFetch();
    },
    startDate() {
      if (this.isActiveTab) this.maybeFetch();
    },
    endDate() {
      if (this.isActiveTab) this.maybeFetch();
    },
    // Watching loadedPosition, not mapLatLon: moving the marker changes nothing here
    // until LOAD DATA is pressed, which is what advances lastPosition. (onLoadDataClick
    // above fires on the same press; both are watched because a press that does not
    // move the pin only toggles the flag.)
    "loadedPosition.latitude"() {
      if (this.isActiveTab) this.maybeFetch();
    },
    "loadedPosition.longitude"() {
      if (this.isActiveTab) this.maybeFetch();
    },
  },
  mounted() {
    this.$store.dispatch("risk/fetchCatalog");
    if (this.isActiveTab) this.maybeFetch();

    if (typeof ResizeObserver !== "undefined" && this.$refs.scroll) {
      this.resizeObserver = new ResizeObserver((entries) => {
        const width = entries[0].contentRect.width;
        this.isNarrow = width < 900;
      });
      this.resizeObserver.observe(this.$refs.scroll);
    }
  },
  // NOT beforeUnmount: this app runs Vue 2.6, which does not recognize that
  // hook name at all (it's Vue 3 only) — Vue 2 silently ignores unknown
  // option keys, so using it here would mean the ResizeObserver never gets
  // disconnected. The project's eslintConfig extends vue3-essential despite
  // the Vue 2 runtime, so this rule misfires on every correct Vue 2 hook;
  // disabled locally rather than adopting the wrong hook or touching the
  // shared eslint config.
  // eslint-disable-next-line vue/no-deprecated-destroyed-lifecycle
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  methods: {
    requestParams() {
      return {
        indicator: this.indicator,
        lat: this.loadedPosition.latitude,
        lon: this.loadedPosition.longitude,
        start_date: this.startDate,
        end_date: this.endDate,
        aggregation: (this.activeIndicator && this.activeIndicator.aggregation) || "weekly",
        threshold: this.threshold,
        units: this.units,
        // Only sent for an indicator the API allows it on (arid's Drought Index
        // </> toggle) — the API 400s on `op` for a fixed-direction indicator, so a
        // blank/omitted param here beats sending one it will reject.
        ...(this.activeIndicator && this.activeIndicator.opSelectable ? { op: this.op } : {}),
        // Same rule for the Heat Index level: only sent where the API accepts it.
        ...(this.activeIndicator && this.activeIndicator.categorySelectable ? { category: this.category } : {}),
        // Same rule again for the minimum daily exposure of the persistence indicator.
        ...(this.activeIndicator && this.activeIndicator.minHoursSelectable ? { min_hours: this.minHours } : {}),
      };
    },
    // Fires for the context around the tab (opening it, LOAD DATA, the dates) and for
    // the "Calculate risk" button. The controls below only change settings.
    maybeFetch() {
      // threshold is required by the API, and axios omits null params rather than
      // sending them — so a null here becomes a 400 with no clue as to why.
      if (!this.canCalculate) return;
      this.$store.dispatch("risk/fetchProbability", this.requestParams());
    },
    calculate() {
      this.maybeFetch();
    },
    // The controls update settings only; nothing is requested until "Calculate risk".
    // A typed threshold commits on blur, which fires before the button's click, so the
    // click always calculates with the value in the box.
    selectIndicator(id) {
      if (id === this.indicator) return;
      this.$store.commit("risk/SET_INDICATOR", id);
    },
    commitThreshold(value) {
      this.$store.commit("risk/SET_THRESHOLD", { indicator: this.indicator, value });
    },
    onThresholdCommit(value) {
      this.commitThreshold(value);
    },
    onPresetClick(value) {
      this.commitThreshold(value);
    },
    onOpClick(value) {
      this.$store.commit("risk/SET_OP", { indicator: this.indicator, value });
    },
    onCategoryClick(value) {
      if (value === this.category) return;
      this.$store.commit("risk/SET_CATEGORY", { indicator: this.indicator, value });
    },
    onMinHoursCommit(value) {
      this.$store.commit("risk/SET_MIN_HOURS", { indicator: this.indicator, value });
    },
    retry() {
      this.$store.dispatch("risk/retry");
    },
    changePeriod() {
      this.$emit("change-period");
    },
    downloadCsv() {
      downloadRiskCsv(this.data, this.activeIndicator);
    },
    downloadChartPng() {
      this.exportChartAsImage("riskChartCard", `${this.indicator}-risk-weekly-chart`);
    },
    // Copied from DataAnalysis.vue's exportChartAsImage, with one fix: set
    // logo.crossOrigin BEFORE logo.src. The original sets it after, so the
    // fetch goes out without CORS mode, taints the canvas, and
    // toDataURL() throws a SecurityError that the outer try/catch
    // swallows — the download silently never happens.
    exportChartAsImage(refName, filename) {
      this.loadingChart = true;
      setTimeout(() => {
        try {
          const chartElement = Array.isArray(this.$refs[refName]) ? this.$refs[refName][0] : this.$refs[refName];
          if (!chartElement) {
            console.error(`Chart with ref "${refName}" not found.`);
            this.loadingChart = false;
            return;
          }

          const options = {
            useCORS: true,
            allowTaint: true,
            scale: window.devicePixelRatio || 1,
            scrollX: 0,
            scrollY: -window.scrollY,
            windowWidth: document.documentElement.offsetWidth,
            windowHeight: document.documentElement.offsetHeight,
          };

          const finish = (canvas) => {
            const link = document.createElement("a");
            link.download = `${filename}.png`;
            link.href = canvas.toDataURL("image/png");
            link.click();
            this.loadingChart = false;
          };

          html2canvas(chartElement.$el || chartElement, options)
            .then((chartCanvas) => {
              const logo = new Image();
              // Set crossOrigin BEFORE src — setting it after (as the
              // DataAnalysis.vue/Dashboard.vue copies of this pattern do)
              // issues the fetch without CORS mode, taints the canvas, and
              // makes toDataURL() throw a SecurityError that gets swallowed
              // by the outer try/catch, so the download silently never
              // happens.
              logo.crossOrigin = "anonymous";
              logo.src = "https://cloud.agroclimate.org/tools/climate-indicators-monitoring/agroclimate_main_logo.png";

              logo.onload = () => {
                const finalCanvas = document.createElement("canvas");
                finalCanvas.width = chartCanvas.width;
                finalCanvas.height = chartCanvas.height;
                const ctx = finalCanvas.getContext("2d");
                ctx.drawImage(chartCanvas, 0, 0);

                const padding = 20 * options.scale;
                const logoWidth = 30 * options.scale;
                const logoHeight = (logo.height / logo.width) * logoWidth;
                ctx.drawImage(logo, finalCanvas.width - logoWidth - padding, padding, logoWidth, logoHeight);

                finish(finalCanvas);
              };
              logo.onerror = () => finish(chartCanvas);
            })
            .catch((err) => {
              console.error("Error generating canvas with html2canvas:", err);
              this.loadingChart = false;
            });
        } catch (error) {
          console.error("Error exporting chart as image:", error);
          this.loadingChart = false;
        }
      }, 200);
    },
  },
};
</script>

<style lang="scss" src="./Risk.scss"></style>
